/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useState } from "react";
import DownloadIcon from "../assets/icons/download.svg";
import axios from "axios";
import { useFlag } from "@unleash/proxy-client-react";
import { useTranslation } from "react-i18next";
import { DownloadAsMenu } from "./DownloadAsMenu";
import {
  DownloadAsOption,
  getDownloadAsUrl,
  getImageInstances,
  hasImage,
  isMultiframe,
} from "@src/utils/downloadAs";

interface IInstance {
  id: string;
  numberOfFrames?: number;
}

interface IImageItem {
  studyId?: string;
  seriesId?: string;
  download?: (studyId?: string, seriesId?: string, instanceId?: string) => void;
  downloadByUrl?: (url: string) => void;
  idx?: number;
  total?: number;
}

export const ImageItem: FC<IImageItem> = ({
  studyId,
  seriesId,
  download,
  downloadByUrl,
  idx,
  total,
}) => {
  const { t } = useTranslation();
  const [instances, setInstances] = useState<IInstance[]>([]);
  const downloadAsEnabled = useFlag("pp-download-multiframe-as");

  const getAllInstances = async () => {
    try {
      const rs = await axios.get(
        `/studies/${studyId}/series/${seriesId}/instances?size=1000000`
      );
      setInstances(rs?.data.content);
    } catch (error) {
      setInstances([]);
    }
  };

  // Non-image instances are left out (unless the series has no image).
  const shownInstances = downloadAsEnabled
    ? getImageInstances(instances)
    : instances;
  const firstInstance = shownInstances[0];
  const frames = firstInstance?.numberOfFrames ?? 0;
  const imageCount = shownInstances.filter((instance) =>
    hasImage(instance.numberOfFrames)
  ).length;
  // Total frames of all images.
  const totalFrames = shownInstances.reduce(
    (sum, instance) => sum + (instance.numberOfFrames ?? 0),
    0
  );
  const anyOtherMultiframe = shownInstances
    .slice(1)
    .some((instance) => isMultiframe(instance.numberOfFrames));
  const shownImageCount = downloadAsEnabled ? imageCount : instances.length;

  const handleSelect = (option: DownloadAsOption) => {
    if (!downloadByUrl || !studyId || !seriesId || !firstInstance?.id) return;
    downloadByUrl(
      getDownloadAsUrl({
        studyId,
        seriesId,
        instanceId: firstInstance.id,
        option,
        frames,
      })
    );
  };

  useEffect(() => {
    if (studyId && seriesId) getAllInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId, studyId]);

  return (
    <div className="img-footer d-flex justify-content-between p-tb-sm p-lr-sm w-100 bg-white">
      <div>
        {idx}/{total} ({shownImageCount}{" "}
        {shownImageCount === 1 ? "Image" : "Images"}
        {downloadAsEnabled && totalFrames > imageCount
          ? ` · ${t("viewer_modal.frame_count", { frames: totalFrames })}`
          : ""}
        )
      </div>
      {downloadAsEnabled && instances.length > 0 ? (
        <DownloadAsMenu
          variant="card"
          imageCount={imageCount}
          frames={frames}
          anyOtherMultiframe={anyOtherMultiframe}
          onSelect={handleSelect}
        />
      ) : (
        <img
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (download) download(studyId, seriesId, instances[0]?.id);
          }}
          src={DownloadIcon}
          alt="DownloadIcon "
        />
      )}
    </div>
  );
};
